import { useCallback, useEffect, useRef } from 'react';

// Extend Window interface for Crisp
declare global {
  interface Window {
    $crisp?: Array<unknown>;
  }
}

/**
 * Hook to control Crisp chat widget programmatically.
 *
 * The Crisp bubble is hidden by default (configured in index.html).
 * Use this hook to show/hide the chat or open it via a custom button.
 */
export function useCrisp() {
  const listenerRegistered = useRef(false);

  // Register event listener to hide bubble when chat is closed
  useEffect(() => {
    if (listenerRegistered.current) return;

    const registerListener = () => {
      if (window.$crisp) {
        window.$crisp.push(["on", "chat:closed", () => {
          window.$crisp?.push(["do", "chat:hide"]);
        }]);
        listenerRegistered.current = true;
      }
    };

    // Try immediately, or wait for Crisp to load
    if (window.$crisp) {
      registerListener();
    } else {
      const interval = setInterval(() => {
        if (window.$crisp) {
          registerListener();
          clearInterval(interval);
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, []);

  /**
   * Opens the Crisp chat widget
   */
  const openChat = useCallback(() => {
    if (window.$crisp) {
      window.$crisp.push(["do", "chat:show"]);
      window.$crisp.push(["do", "chat:open"]);
    }
  }, []);

  /**
   * Closes the Crisp chat widget
   */
  const closeChat = useCallback(() => {
    if (window.$crisp) {
      window.$crisp.push(["do", "chat:close"]);
    }
  }, []);

  /**
   * Hides the Crisp chat bubble completely
   */
  const hideBubble = useCallback(() => {
    if (window.$crisp) {
      window.$crisp.push(["do", "chat:hide"]);
    }
  }, []);

  /**
   * Shows the Crisp chat bubble
   */
  const showBubble = useCallback(() => {
    if (window.$crisp) {
      window.$crisp.push(["do", "chat:show"]);
    }
  }, []);

  /**
   * Toggles the chat open/closed
   */
  const toggleChat = useCallback(() => {
    if (window.$crisp) {
      window.$crisp.push(["do", "chat:toggle"]);
    }
  }, []);

  return {
    openChat,
    closeChat,
    hideBubble,
    showBubble,
    toggleChat,
  };
}
